import React, { useState, useEffect, useRef } from "react";
import { object, func, string } from "prop-types";
import get from "lodash/get";
import throttle from "lodash/throttle";
import { useSelector } from "react-redux";
import { BrowserView, MobileView } from "react-device-detect";

import { useDeviceType } from "../../customHooks/useDeviceType";
import VideoStoryHeader from "../../molecules/VideoStoryHeader";
import { SocialShare } from "@quintype/components";
import { SocialShareTemplate } from "../../../arrow/components/Molecules/SocialShareTemplate";
import { StoryAuthors, Headline, PublishDateAndTime, ReadMoreToggle, BreadCrumb } from "../../atoms";
import { storyFields } from "../../../constants";
import ThreeColGrid from "../../../arrow/components/Rows/ThreeColGrid";
import { getPrimaryVideo, getVideoStoryCards } from "../../utils";

import { DfpComponent } from "../../ads/dfp-component";
import { Text } from "../../../arrow";

import "./video-story.m.css";

const charLimits = {
  desktop: 180,
  tablet: 160,
  mobile: 60,
};

const TextWithReadMore = ({ textElement, deviceType }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [plainText, setPlainText] = useState(textElement.text);
  const [shouldShowReadMore, setShouldShowReadMore] = useState(false);
  const [isSSR, setIsSSR] = useState(true);
  //const charLimit = charLimits[deviceType];

  /*useEffect(() => {
    if (typeof window !== "undefined" && typeof document !== "undefined") {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = textElement.text;
      const textContent = tempDiv.innerText || tempDiv.textContent;
      setPlainText(textContent);
      setShouldShowReadMore(textContent.length > charLimit);
      setIsSSR(false);
    }
  }, [textElement, charLimit]);*/

  const toggleDescription = () => {
    setIsExpanded(!isExpanded);
  };

  /*const truncatedHTML = isExpanded
    ? textElement.text
    : plainText?.slice(0, charLimit) + (plainText?.length > charLimit ? "..." : "");*/

  const truncatedHTML = textElement.text;

  return (
    <div styleName={`text-container ${isExpanded || isSSR ? "expanded" : ""}`}>
      {isExpanded || isSSR ? (
        <Text element={textElement} />
      ) : (
        <span dangerouslySetInnerHTML={{ __html: truncatedHTML }} />
      )}
      {shouldShowReadMore && (
        <ReadMoreToggle textElement={textElement} isExpanded={isExpanded} onClick={toggleDescription} />
      )}
    </div>
  );
};

TextWithReadMore.propTypes = {
  textElement: object,
  deviceType: string,
};

const VideoStory = ({ story, moreVideos }) => {
  const headerHeight = 170;

  const [isExpanded, setIsExpanded] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const placeholderRef = useRef(null);

  const { headline = "", authors = [] } = story;
  const primaryVideo = getPrimaryVideo(story.cards);
  const cards = primaryVideo ? getVideoStoryCards(story.cards, primaryVideo.id) : story.cards;
  const element = get(cards, ["0", "story-elements", [0]], {});
  const textElement = element.type === "text" ? element : {};
  const deviceType = useDeviceType();

  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const networkId = get(qtState, ["config", "ads-config", "network_id"], {});

  useEffect(() => {
    const throttledHandleScroll = throttle(() => {
      handleScroll();
    }, 100);

    if (window && window.innerWidth < 768) {
      window.addEventListener("scroll", throttledHandleScroll);
    }

    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
    };
  }, []);

  const handleScroll = () => {
    if (videoRef.current && containerRef.current) {
      const containerBottom = containerRef.current.getBoundingClientRect().bottom;
      if (containerBottom <= videoRef.current.offsetHeight + headerHeight) {
        if (!placeholderRef.current) {
          placeholderRef.current = document.createElement("div");
          placeholderRef.current.style.width = `${videoRef.current.offsetWidth}px`;
          placeholderRef.current.style.height = `${videoRef.current.offsetHeight}px`;
          videoRef.current.parentNode.insertBefore(placeholderRef.current, videoRef.current);
        }
        videoRef.current.classList.add("video-sticky");
      } else {
        if (placeholderRef.current) {
          placeholderRef.current.remove();
          placeholderRef.current = null;
        }
        videoRef.current.classList.remove("video-sticky");
      }
    }
  };

  const toggleDescription = () => {
    setIsExpanded(!isExpanded);
  };

  const footerSlot = (
    <>
      <BrowserView>
        <DfpComponent
          adStyleName="ad-slot-size-300x250"
          id="video-page-ad"
          path={`/${networkId}/RPSG_Opn_Desktop/RPSG_Opn_VID_mREC`}
          adSlotConfigName="mRec_ad"
          showText={true}
        />
      </BrowserView>
    </>
  );

  return (
    <>
      <div styleName="container">
        <div styleName="video-story-wrapper" ref={containerRef} className="video-container">
          <VideoStoryHeader story={story} primaryVideo={primaryVideo} headline={headline} videoRef={videoRef} />
          <div styleName="content">
            <div styleName="headline">
              <Headline type={1} customStyleName="h1-style">
                {headline}
              </Headline>
            </div>
            <div styleName="author-timestamp-wrapper">
              <div styleName="author-timestamp">
                <StoryAuthors storyAuthor={authors} customStyleName={"authors-name"} />
                <div styleName="date-and-time">
                  <PublishDateAndTime publishTime={story?.["published-at"]} />
                </div>
              </div>
              <span styleName="video-story-share" className="content-style">
                <SocialShare
                  template={SocialShareTemplate}
                  fullUrl={encodeURI(story.url)}
                  title={story.headline}
                  shareIconId={`share-icon-story-share-${story.id}`}
                  story={story}
                />
              </span>
            </div>
            <div styleName="synopsis-text">
              {textElement?.text && (
                <TextWithReadMore
                  textElement={textElement}
                  toggleDescription={toggleDescription}
                  deviceType={deviceType}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {moreVideos?.items?.length > 0 ? (
        <div className="container" styleName="more-videos">
          <ThreeColGrid
            collection={moreVideos}
            customSlug={"/videos"}
            customCollectionName="All Video News"
            config={{
              buttonText: "Read More",
              footerButton: "SubsequentLoadCount",
              showAuthor: true,
              showTime: true,
              customUrlPath: "videos",
              slotConfig: [
                {
                  type: "ad",
                  component: () => footerSlot,
                },
              ],
            }}
            isLoadMoreVisible={true}
            initialLoadCount={6}
            page="home-page"
            template="customUrlPath"
            isMoreVideos
            showAuthorContent={false}
            fromStory={story}
          />
        </div>
      ) : null}
      <MobileView>
        <DfpComponent
          adStyleName="ad-slot-size-300x250"
          id="video-page-mobile-ad"
          path={`/${networkId}/RPSG_Opn_Mob/RPSG_Opn_VID_mREC`}
          adSlotConfigName="mRec_ad"
          showText={true}
        />
      </MobileView>
    </>
  );
};

VideoStory.propTypes = {
  story: object,
  config: object,
  adWidget: func,
  adPlaceholder: object,
  hasAccess: func,
};

export default VideoStory;
